// Small dialog to let players opt-in to a public handle used on leaderboards.
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserCircle2 } from "lucide-react";
import { syncPlayerState } from "@/lib/playerSync";
import { getEmail } from "@/lib/pro";
import {
  getHandleCooldownRemainingMs,
  markHandleChange,
  HANDLE_GEMS,
  PRISMATIC_GEM,
  getHandleGem,
  setHandleGem,
  getGemInventory,
  tradeForPrismatic,
} from "@/lib/achievementsExtra";

const KEY = "ppe_public_handle_v1";

export function getPublicHandle() {
  return localStorage.getItem(KEY) || "";
}

export function setPublicHandle(handle) {
  const clean = (handle || "").trim().slice(0, 20);
  if (clean) localStorage.setItem(KEY, clean);
  else localStorage.removeItem(KEY);
  return clean;
}

export default function HandleDialog({ open, onOpenChange, onSaved }) {
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldownMs, setCooldownMs] = useState(0);
  const [gem, setGem] = useState("");
  const [inventory, setInventory] = useState({});

  useEffect(() => {
    if (open) {
      setHandle(getPublicHandle());
      setCooldownMs(getHandleCooldownRemainingMs());
      setGem(getHandleGem());
      setInventory(getGemInventory());
    }
  }, [open]);

  // Tick the cooldown down while the dialog is open
  useEffect(() => {
    if (!open || cooldownMs <= 0) return;
    const id = setInterval(() => setCooldownMs(getHandleCooldownRemainingMs()), 500);
    return () => clearInterval(id);
  }, [open, cooldownMs]);

  const save = async () => {
    if (cooldownMs > 0) {
      toast.error(`WAIT ${Math.ceil(cooldownMs / 1000)}S BEFORE CHANGING AGAIN`);
      return;
    }
    const clean = handle.trim().slice(0, 20);
    // Basic sanitation: alphanumerics + . _ -
    if (clean && !/^[A-Za-z0-9._-]{2,20}$/.test(clean)) {
      toast.error("2-20 characters · A-Z / 0-9 / . _ -");
      return;
    }
    setBusy(true);
    const prev = getPublicHandle();
    const prevGem = getHandleGem();
    setPublicHandle(clean);
    setHandleGem(gem);
    if (getEmail()) {
      const res = await syncPlayerState();
      if (!res.ok) {
        setPublicHandle(prev);
        setHandleGem(prevGem);
        setBusy(false);
        toast.error(res.error || "Handle rejected by moderation filter");
        return;
      }
    }
    markHandleChange();
    setBusy(false);
    toast.success(clean ? `HANDLE SET · ${clean}` : "HANDLE CLEARED · masked email restored");
    onSaved && onSaved(clean);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0b0b0f] border border-white/10 max-w-md"
        data-testid="handle-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display text-2xl tracking-tighter flex items-center gap-2">
            <UserCircle2 size={20} className="text-[#00F0FF]" /> PUBLIC HANDLE
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs tracking-wider">
            Show a real name on the wager + buggy leaderboards instead of your masked email.
            Leave blank to stay anonymous.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-3">
          <Input
            data-testid="handle-input"
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="e.g. NeonRider"
            maxLength={20}
            className="bg-transparent border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#00F0FF] font-mono"
          />
          <div className="text-[10px] text-white/40 tracking-wider">
            2-20 chars · letters, numbers, . _ - only
          </div>
          {(Object.keys(inventory).length > 0) && (
            <div className="mt-4">
              <div className="text-[10px] text-white/50 tracking-[0.3em] mb-2">HANDLE GEM</div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="handle-gem-none"
                  onClick={() => setGem("")}
                  className={`px-3 py-1 border text-[10px] tracking-[0.3em] transition-colors ${gem === "" ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/20 text-white/50 hover:border-white/40"}`}
                >
                  NONE
                </button>
                {[...HANDLE_GEMS, PRISMATIC_GEM].filter((g) => (inventory[g.id] || 0) > 0).map((g) => (
                  <button
                    key={g.id}
                    data-testid={`handle-gem-${g.id}`}
                    onClick={() => setGem(g.id)}
                    className={`px-3 py-1 border text-[10px] tracking-[0.3em] transition-colors ${gem === g.id ? "" : "border-white/20 text-white/60 hover:border-white/40"}`}
                    style={gem === g.id ? { borderColor: g.color, color: g.color, boxShadow: `0 0 12px ${g.color}66` } : {}}
                  >
                    {g.symbol} {g.name} <span className="opacity-60">×{inventory[g.id]}</span>
                  </button>
                ))}
              </div>
              {/* Trade panel: convert 3× any base gem → 1× prismatic */}
              {HANDLE_GEMS.some((g) => (inventory[g.id] || 0) >= 3) && (
                <div className="mt-3 border border-[#B0F0FF]/40 bg-[#B0F0FF]/5 p-2">
                  <div className="text-[10px] tracking-[0.3em] text-[#B0F0FF] mb-2">TRADE 3× → 1× ◈ PRISMATIC</div>
                  <div className="flex flex-wrap gap-2">
                    {HANDLE_GEMS.filter((g) => (inventory[g.id] || 0) >= 3).map((g) => (
                      <button
                        key={`trade-${g.id}`}
                        data-testid={`trade-gem-${g.id}`}
                        onClick={() => {
                          const res = tradeForPrismatic(g.id);
                          if (!res.ok) { toast.error(res.error || "Trade failed"); return; }
                          setInventory(getGemInventory());
                          toast.success(`TRADED 3× ${g.symbol} → 1× ◈ PRISMATIC`);
                        }}
                        className="px-2 py-1 border text-[10px] tracking-[0.3em] transition-colors"
                        style={{ borderColor: g.color, color: g.color }}
                      >
                        3× {g.symbol} → ◈
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[9px] text-white/40 tracking-wider mt-2">Buy gems in the Neon Shop · combine 3-of-a-kind for the rare Prismatic gem.</div>
            </div>
          )}
          {cooldownMs > 0 && (
            <div className="text-[10px] text-[#FFD400] tracking-widest" data-testid="handle-cooldown">
              COOLDOWN · {Math.ceil(cooldownMs / 1000)}s
            </div>
          )}
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button
            data-testid="handle-cancel"
            onClick={() => onOpenChange(false)}
            className="text-[10px] tracking-[0.3em] px-3 py-1.5 border border-white/20 text-white/60 hover:border-white/40 transition-colors"
          >
            CANCEL
          </button>
          <button
            data-testid="handle-save"
            disabled={busy || cooldownMs > 0}
            onClick={save}
            className="text-[10px] tracking-[0.3em] px-3 py-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors disabled:opacity-40"
          >
            SAVE
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
